import { useEffect } from "react";

import { getIsTouchDevice } from "@/utils/getIsTouchDevice";
import { trackTouchScroll } from "@/utils/touchScroll";

import type { Editor } from "@tiptap/core";

/**
 * Keeps iOS fling scrolling alive across editor updates.
 *
 * Without CSS scroll anchoring (Safari), ProseMirror re-anchors the view after any
 * update that shifts content above it (lazy math rendering, remote edits, measured
 * sizes) by writing `scrollTop`. Any programmatic scroll stops an iOS fling dead, and
 * one during the drag makes iOS snap back to it on release instead of flinging.
 * ProseMirror skips that step when the editor's `style.overflowAnchor` is set, so we
 * set it from finger down until the momentum scroll stops.
 */
export function useKeepTouchMomentum(editor: Editor | null) {
  useEffect(() => {
    if (!editor || !getIsTouchDevice()) return undefined;
    let style: CSSStyleDeclaration | undefined;

    return trackTouchScroll({
      onStart: () => {
        // Before mount, `editor.view` is a proxy that throws on `dom`.
        if (!editor.isInitialized || editor.isDestroyed) return;
        ({ style } = editor.view.dom);
        Reflect.defineProperty(style, "overflowAnchor", { value: "none", configurable: true });
      },
      onSettle: () => {
        if (style) Reflect.deleteProperty(style, "overflowAnchor");
        style = undefined;
      },
    });
  }, [editor]);
}
